/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import {
    ChoiceGroup,
    IChoiceGroupOption,
    IChoiceGroupOptionProps,
    IChoiceGroupOptionStyleProps,
    IChoiceGroupOptionStyles
} from '@fluentui/react/lib/ChoiceGroup';
import { Stack } from '@fluentui/react/lib/Stack';
import { IRenderFunction, classNamesFunction } from '@fluentui/react/lib/Utilities';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { ExternalLink } from 'src/core/components/ExternalLink';
import { LuisText } from 'src/core/components/LuisText';
import { LuisTooltip } from 'src/core/components/LuisTooltip';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { logDemoButtonClick } from 'src/core/services/telemetry/eventsNamesTemplate';
import { Region } from 'src/core/types/regionTypes';
import { SummarizationType } from 'src/languageStudio/appShell/prebuiltDemos/summarizationDemo/SummarizationDemoPage';
import { commonDemosMessages } from 'src/languageStudio/appShell/prebuiltDemos/utils/commonDemoMessages';
import {
    summarizationOverviewLink, // summarizationRequestAccessLink,
    textAnalyticsPricingLink
} from 'src/languageStudio/appShell/prebuiltDemos/utils/demosSupportLinks';

type TextSummarizationAspectsOptionsProps = {
    currentRegion: Region;
    isAbstractiveSummaryEnabled: boolean;
    selectedSummarizationType: SummarizationType;
    onSummarizationTypeChange: (newType: SummarizationType) => void;
};

const messages = defineMessages({
    bothTypes: { id: 'languageStudio.textSummarizationAspectsOptions.bothTypes', defaultMessage: 'Both' },
    summaryType: { id: 'languageStudio.textSummarizationAspectsOptions.summaryType', defaultMessage: 'Summary Types' },
    /* approvedAccess: {
        id: 'languageStudio.textSummarizationAspectsOptions.approvedAccess',
        defaultMessage: 'Request near real time response'
    }, */
    summaryTypeLink: {
        id: 'languageStudio.textSummarizationAspectsOptions.summaryTypeLink',
        defaultMessage: 'Learn more about pricing'
    },
    extractiveSummaryType: {
        id: 'languageStudio.textSummarizationAspectsOptions.extractiveSummaryType',
        defaultMessage: 'Extractive summarization'
    },
    extractiveSummaryTypeDescription: {
        id: 'languageStudio.textSummarizationAspectsOptions.extractiveSummaryTypeDescription',
        defaultMessage: 'Produces a summary by extracting salient sentences within a document'
    },
    abstractiveSummaryType: {
        id: 'languageStudio.textSummarizationAspectsOptions.abstractiveSummaryType',
        defaultMessage: 'Abstractive summarization'
    },
    abstractiveSummaryTypeDescription: {
        id: 'languageStudio.textSummarizationAspectsOptions.abstractiveSummaryTypeDescription',
        defaultMessage: 'Generates a summary with novel sentences for a document'
    },
    summaryTypeDescription: {
        id: 'languageStudio.textSummarizationAspectsOptions.summaryTypeDescription',
        defaultMessage: 'These options may incur additional costs'
    },
    bothTypesDescription: {
        id: 'languageStudio.textSummarizationAspectsOptions.bothTypesDescription',
        defaultMessage: 'Extractive summary and Abstractive summary side by side'
    }
});

const descriptionContentStyles = { root: { marginLeft: '27px' } };
const optionStyles = (isAbstractiveSummaryEnabled: boolean) =>
    classNamesFunction<IChoiceGroupOptionStyleProps, IChoiceGroupOptionStyles>()({
        field: { cursor: isAbstractiveSummaryEnabled ? 'pointer' : 'not-allowed' }
    });

export const TextSummarizationAspectsOptions = (props: TextSummarizationAspectsOptionsProps) => {
    const { currentRegion, selectedSummarizationType, isAbstractiveSummaryEnabled, onSummarizationTypeChange } = props;
    const { localize } = useLocalization();

    const notSupportedResourceRegionContent = (
        <LuisText>
            {localize(commonDemosMessages.resourceRegionDetailedError, { region: currentRegion })}&nbsp;
            <ExternalLink href={summarizationOverviewLink}>{localize(commonDemosMessages.seeRegionsLink)}</ExternalLink>&nbsp;
            {localize(commonDemosMessages.resourceRegionDetailedErrorPart2)}
        </LuisText>
    );

    const renderChoiceGroupField: IRenderFunction<IChoiceGroupOption & IChoiceGroupOptionProps> = (
        groupProps: IChoiceGroupOption & IChoiceGroupOptionProps,
        groupDefaultRenderer: (p: IChoiceGroupOption & IChoiceGroupOptionProps) => JSX.Element | null
    ) => (
        <LuisTooltip hidden={!groupProps.disabled} content={notSupportedResourceRegionContent}>
            {groupDefaultRenderer(groupProps)}
        </LuisTooltip>
    );

    const viewOptions: IChoiceGroupOption[] = [
        {
            key: 'both',
            role: 'radio',
            text: localize(messages.bothTypes),
            onRenderField: renderChoiceGroupField,
            disabled: !isAbstractiveSummaryEnabled,
            styles: optionStyles(isAbstractiveSummaryEnabled),
            onRenderLabel: (labelProps, labelDefaultRenderer) =>
                renderChoiceGroupDescription(labelProps, labelDefaultRenderer, localize(messages.bothTypesDescription))
        },
        {
            role: 'radio',
            key: 'extractive',
            text: localize(messages.extractiveSummaryType),
            onRenderLabel: (labelProps, labelDefaultRenderer) =>
                renderChoiceGroupDescription(labelProps, labelDefaultRenderer, localize(messages.extractiveSummaryTypeDescription))
        },
        {
            role: 'radio',
            key: 'abstractive',
            onRenderField: renderChoiceGroupField,
            disabled: !isAbstractiveSummaryEnabled,
            text: localize(messages.abstractiveSummaryType),
            styles: optionStyles(isAbstractiveSummaryEnabled),
            onRenderLabel: (p, defaultRenderer) =>
                renderChoiceGroupDescription(p, defaultRenderer, localize(messages.abstractiveSummaryTypeDescription))
        }
    ];

    const renderChoiceGroupDescription = (
        optionProps: IChoiceGroupOptionProps,
        optionDefaultRenderer: (props?: IChoiceGroupOptionProps) => JSX.Element,
        description: string
    ) => (
        <Stack tokens={{ childrenGap: 8 }}>
            {optionDefaultRenderer(optionProps)}
            <LuisText isDisabled={optionProps.disabled} styles={descriptionContentStyles} variant="small">
                {description}
            </LuisText>
        </Stack>
    );

    const handleChangeType = (typeKey: string) => {
        logDemoButtonClick('Summarization information', 'SummaryType', typeKey);
        onSummarizationTypeChange(typeKey as SummarizationType);
    };

    return (
        <Stack styles={{ root: { flex: 2 } }} tokens={{ childrenGap: 17 }}>
            <LuisText fontWeight={600}>{localize(messages.summaryType)} </LuisText>

            <LuisText fontWeight={400}>
                {localize(messages.summaryTypeDescription)}
                &nbsp;
                <ExternalLink href={textAnalyticsPricingLink}>{localize(messages.summaryTypeLink)}</ExternalLink>
            </LuisText>

            {/* <ExternalLink styles={descriptionContentStyles} variant="small" href={summarizationRequestAccessLink}>
                {localize(messages.approvedAccess)}
            </ExternalLink> */}

            <ChoiceGroup
                options={viewOptions}
                defaultSelectedKey="both"
                selectedKey={selectedSummarizationType}
                onChange={(_, type) => handleChangeType(type.key)}
            />
        </Stack>
    );
};
